import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { components } from '../generated/api';
import { trackUserJourney } from '../utils/datadog';
import './HomePage.css';

type BotDetectionRequest = components['schemas']['BotDetectionRequest'];
type BotDetectionResponse = components['schemas']['BotDetectionResponse'];

const HomePage: React.FC = () => {
  useEffect(() => {
    document.title = 'Bitsburg Bank - Home';

    // Track user journey step
    trackUserJourney('home_page_visited', {
      previous_page: document.referrer || 'direct',
      timestamp: new Date().toISOString(),
    });

    // Check for null reference error occurrence
    if (Math.random() < 0.1) {
      try {
        window.DD_LOGS?.logger?.info('Checking for null reference error on home page');
        const nullValue: any = null;
        nullValue.someProperty.access();
      } catch (error) {
        window.DD_LOGS?.logger?.error('Null reference error on home page', {
          error: error instanceof Error ? error.message : 'Unknown error',
        });
        window.DD_RUM?.addError(error);
      }
    }

    // Perform bot detection check when the home page loads
    const performBotDetection = async () => {
      try {
        const botDetectionRequest: BotDetectionRequest = {
          userAgent: navigator.userAgent,
        };

        const response = await fetch('/api/security/bot/detection', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(botDetectionRequest),
        });

        (await response.json()) as BotDetectionResponse;
      } catch {
        // Intentionally catch any errors
      }
    };

    performBotDetection();
  }, []);
  return (
    <>
      {/* Hero Section */}
      <div className="hero-section bg-primary text-white">
        <div className="container">
          <div className="row align-items-center min-vh-50">
            <div className="col-lg-7">
              <h1 className="display-4 fw-bold mb-4">Banking that works for you</h1>
              <p className="lead mb-4">
                Experience modern banking with Bitsburg Bank. Secure, convenient, and tailored to your financial needs.
              </p>
              <div className="hero-buttons">
                <Link to="/login" className="btn btn-light btn-lg me-3 mb-2" id="home-login-hero-btn">
                  <i className="bi bi-person-circle me-2"></i>Login to Online Banking
                </Link>
              </div>
            </div>
            <div className="col-lg-5 text-center">
              <div className="hero-image">
                <img src="/images/bitsburg-bank.png" alt="Bitsburg Bank" className="img-fluid content-image" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <section id="services" className="py-5">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-12">
              <h2 className="display-5 fw-bold mb-3 text-datadog-purple">Our Banking Services</h2>
              <p className="lead text-muted">Everything you need for your financial journey</p>
            </div>
          </div>
          <div className="row g-4">
            <div className="col-md-4">
              <div className="bank-card h-100">
                <div className="bank-card-body text-center">
                  <div className="service-icon mb-3">
                    <i className="bi bi-credit-card text-datadog-purple" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h4 className="card-title fw-bold">Checking Accounts</h4>
                  <p className="card-text text-muted">
                    Enjoy fee-free checking with mobile banking, online bill pay, and 24/7 access to your money.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="bank-card h-100">
                <div className="bank-card-body text-center">
                  <div className="service-icon mb-3">
                    <i className="bi bi-piggy-bank text-datadog-purple" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h4 className="card-title fw-bold">Savings Accounts</h4>
                  <p className="card-text text-muted">
                    Grow your money with competitive interest rates and flexible savings options tailored to your goals.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="bank-card h-100">
                <div className="bank-card-body text-center">
                  <div className="service-icon mb-3">
                    <i className="bi bi-credit-card-2-front text-datadog-purple" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h4 className="card-title fw-bold">Credit Cards</h4>
                  <p className="card-text text-muted">
                    Earn rewards with every purchase! Get up to <strong>50,000 bonus points</strong> when you sign up
                    today!
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="bank-card h-100">
                <div className="bank-card-body text-center">
                  <div className="service-icon mb-3">
                    <i className="bi bi-car-front text-datadog-purple" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h4 className="card-title fw-bold">Auto Loans</h4>
                  <p className="card-text text-muted">
                    Drive your dream car with competitive rates and flexible terms. Get pre-approved in minutes.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="bank-card h-100">
                <div className="bank-card-body text-center">
                  <div className="service-icon mb-3">
                    <i className="bi bi-graph-up-arrow text-datadog-purple" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h4 className="card-title fw-bold">Asset Management</h4>
                  <p className="card-text text-muted">
                    Professional investment management tailored to your goals. Diversified portfolios and expert
                    guidance for your financial future.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="bank-card h-100">
                <div className="bank-card-body text-center">
                  <div className="service-icon mb-3">
                    <i className="bi bi-briefcase text-datadog-purple" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h4 className="card-title fw-bold">Business Loans</h4>
                  <p className="card-text text-muted">
                    Fuel your business growth with flexible financing solutions. Competitive rates, fast approval, and
                    dedicated business banking support.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Bitsburg Bank Section */}
      <section className="py-4">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="bank-card">
                <div className="bank-card-body">
                  <h5 className="fw-bold text-datadog-purple mb-4 text-center">
                    <i className="bi bi-star me-2"></i>
                    Why Choose Bitsburg Bank?
                  </h5>
                  <div className="row text-center">
                    <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                      <i className="bi bi-shield-check text-datadog-purple fs-2 mb-2"></i>
                      <h6 className="fw-bold">Secure Banking</h6>
                      <p className="text-muted small">Bank-level encryption and fraud protection</p>
                    </div>
                    <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                      <i className="bi bi-clock text-datadog-purple fs-2 mb-2"></i>
                      <h6 className="fw-bold">24/7 Access</h6>
                      <p className="text-muted small">Online and mobile banking available anytime</p>
                    </div>
                    <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                      <i className="bi bi-credit-card text-datadog-purple fs-2 mb-2"></i>
                      <h6 className="fw-bold">No Hidden Fees</h6>
                      <p className="text-muted small">Transparent pricing with no surprise charges</p>
                    </div>
                    <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                      <i className="bi bi-headset text-datadog-purple fs-2 mb-2"></i>
                      <h6 className="fw-bold">Expert Support</h6>
                      <p className="text-muted small">Dedicated customer service team</p>
                    </div>
                    <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                      <i className="bi bi-lightning-charge text-datadog-purple fs-2 mb-2"></i>
                      <h6 className="fw-bold">Instant Transfers</h6>
                      <p className="text-muted small">Lightning-fast money transfers and payments</p>
                    </div>
                    <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                      <i className="bi bi-award text-datadog-purple fs-2 mb-2"></i>
                      <h6 className="fw-bold">Paw-some Rewards</h6>
                      <p className="text-muted small">Earn treats and rewards with every transaction</p>
                    </div>
                    <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                      <i className="bi bi-phone text-datadog-purple fs-2 mb-2"></i>
                      <h6 className="fw-bold">Mobile Deposit</h6>
                      <p className="text-muted small">Snap a photo to deposit checks instantly</p>
                    </div>
                    <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                      <i className="bi bi-heart text-datadog-purple fs-2 mb-2"></i>
                      <h6 className="fw-bold">Pet-Friendly Banking</h6>
                      <p className="text-muted small">Special accounts for your furry family members</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-5 bg-white">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h2 className="display-6 fw-bold mb-4 text-datadog-purple">Your Security is Our Priority</h2>
              <div className="security-features">
                <div className="d-flex align-items-center mb-3">
                  <div className="me-3">
                    <i className="bi bi-shield-check text-success fs-3"></i>
                  </div>
                  <div>
                    <h5 className="fw-bold">Bank-Level Security</h5>
                    <p className="text-muted mb-0">
                      Your data is protected with AES‑256 encryption, secure TLS connections, and multi-factor
                      authentication.
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-center mb-3">
                  <div className="me-3">
                    <i className="bi bi-search text-success fs-3"></i>
                  </div>
                  <div>
                    <h5 className="fw-bold">24/7 Fraud Monitoring</h5>
                    <p className="text-muted mb-0">
                      Advanced fraud detection systems monitor your accounts around the clock.
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-center mb-3">
                  <div className="me-3">
                    <i className="bi bi-award text-success fs-3"></i>
                  </div>
                  <div>
                    <h5 className="fw-bold">Data Privacy Standards</h5>
                    <p className="text-muted mb-0">
                      We comply with global privacy regulations including GDPR, ensuring your personal data is
                      protected, and handled responsibly.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6 text-center">
              <div className="security-image">
                <img
                  src="/images/bitsburg-security.png"
                  alt="Bitsburg Bank Security"
                  className="img-fluid content-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-5 text-white bg-datadog-purple">
        <div className="container text-center">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <h2 className="display-6 fw-bold mb-4">Ready to get started?</h2>
              <p className="lead mb-4">
                Join thousands of satisfied customers who trust Bitsburg Bank with their financial future.
              </p>
              <Link to="/enroll" className="btn btn-light btn-lg me-3" id="home-enroll-cta-btn">
                <i className="bi bi-person-plus me-2"></i>Open an Account
              </Link>
              <Link to="/login" className="btn btn-outline-light btn-lg" id="home-login-cta-btn">
                <i className="bi bi-box-arrow-in-right me-2"></i>Sign In
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
